import React, { useState, useEffect, useRef, useCallback } from 'react';
import ContractCard from './ContractCard';
import StoryProgressBar from './StoryProgressBar';
import { filterContractsByExpiration } from '../../utils/screenerDataHelpers';
import '../../styles/MobileScreener.css';

/**
 * Instagram Stories-style view with grouped progress bars by ticker
 * Full-screen, no-scroll layout with arrow button navigation
 */
const TickerStoryView = ({
  ticker: initialTicker,
  resultsGrouped,
  selectedExpirations,
  expirationSettings,
  onClose,
  onContractGainUpdate,
  onUpdateAll,
  originalSettings
}) => {
  // Group contracts by ticker after filtering
  const [tickerGroups, setTickerGroups] = useState({});
  const [allTickers, setAllTickers] = useState([]);
  const [currentTicker, setCurrentTicker] = useState(initialTicker);
  const [currentIndexInTicker, setCurrentIndexInTicker] = useState(0);
  const [currentGlobalIndex, setCurrentGlobalIndex] = useState(0);
  const [allCards, setAllCards] = useState([]);

  // Refs to maintain current values for event handlers
  const currentIndexRef = useRef(0);
  const allCardsRef = useRef([]);
  const tickerGroupsRef = useRef({});

  // Keep refs synchronized with state
  useEffect(() => {
    currentIndexRef.current = currentGlobalIndex;
  }, [currentGlobalIndex]);

  useEffect(() => {
    allCardsRef.current = allCards;
  }, [allCards]);

  useEffect(() => {
    tickerGroupsRef.current = tickerGroups;
  }, [tickerGroups]);

  // Process and group contracts by ticker
  useEffect(() => {
    // Save current position BEFORE rebuilding (to preserve position after refresh)
    const savedTicker = currentTicker;
    const savedContract = allCards[currentGlobalIndex];

    const groups = {};
    const flatCards = [];
    const tickers = [];

    // Start with the initial ticker
    if (resultsGrouped[initialTicker]) {
      const filtered = filterContractsByExpiration(
        resultsGrouped[initialTicker],
        selectedExpirations
      );
      if (filtered.length > 0) {
        groups[initialTicker] = filtered;
        tickers.push(initialTicker);
        filtered.forEach((contract, idx) => {
          flatCards.push({
            ...contract,
            ticker: initialTicker,
            globalIndex: `${initialTicker}-${idx}`
          });
        });
      }
    }

    // Add other tickers in order
    Object.keys(resultsGrouped).forEach((ticker) => {
      if (ticker !== initialTicker) {
        const filtered = filterContractsByExpiration(
          resultsGrouped[ticker],
          selectedExpirations
        );
        if (filtered.length > 0) {
          groups[ticker] = filtered;
          tickers.push(ticker);
          filtered.forEach((contract, idx) => {
            flatCards.push({
              ...contract,
              ticker: ticker,
              globalIndex: `${ticker}-${idx}`
            });
          });
        }
      }
    });

    setTickerGroups(groups);
    setAllTickers(tickers);
    setAllCards(flatCards);

    // Try to find the same contract after rebuilding (preserve position on refresh)
    if (savedContract && savedTicker) {
      const savedStrike = savedContract.Strike || savedContract.strike;
      const savedExpiration = savedContract.Expiration || savedContract.expiration;

      const newIndex = flatCards.findIndex(c =>
        c.ticker === savedTicker &&
        (c.Strike || c.strike) === savedStrike &&
        (c.Expiration || c.expiration) === savedExpiration
      );

      if (newIndex >= 0) {
        // Found the same contract - preserve position
        const contracts = groups[savedTicker] || [];
        const indexInTicker = contracts.findIndex(
          c => (c.Strike || c.strike) === savedStrike &&
               (c.Expiration || c.expiration) === savedExpiration
        );

        setCurrentGlobalIndex(newIndex);
        setCurrentTicker(savedTicker);
        setCurrentIndexInTicker(Math.max(0, indexInTicker));
        return; // Don't reset to initial
      }
    }

    // Only reset to initial if contract not found (e.g., first load)
    setCurrentTicker(initialTicker);
    setCurrentIndexInTicker(0);
    setCurrentGlobalIndex(0);
  }, [initialTicker, resultsGrouped, selectedExpirations]);

  // Navigate to specific contract - using useCallback for stable reference
  const navigateToContract = useCallback((globalIndex) => {
    if (globalIndex < 0 || globalIndex >= allCardsRef.current.length) {
      // Exit if we're at the end
      if (globalIndex >= allCardsRef.current.length) {
        onClose();
      }
      return;
    }

    const card = allCardsRef.current[globalIndex];
    const ticker = card.ticker;
    const contracts = tickerGroupsRef.current[ticker] || [];
    const indexInTicker = contracts.findIndex(
      c => c.Strike === card.Strike && c.Expiration === card.Expiration
    );

    setCurrentGlobalIndex(globalIndex);
    setCurrentTicker(ticker);
    setCurrentIndexInTicker(Math.max(0, indexInTicker));
  }, [onClose]);

  // Handle navigation (swipe or tap)
  const handleNext = () => {
    const nextIndex = currentGlobalIndex + 1;
    if (nextIndex >= allCards.length) {
      // Exit at the end
      onClose();
    } else {
      navigateToContract(nextIndex);
    }
  };

  const handlePrevious = () => {
    const prevIndex = currentGlobalIndex - 1;
    if (prevIndex >= 0) {
      navigateToContract(prevIndex);
    }
  };

  // Keyboard navigation - using refs to avoid stale closures
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const prevIndex = currentIndexRef.current - 1;
        if (prevIndex >= 0) {
          navigateToContract(prevIndex);
        }
      } else if (e.key === 'ArrowRight') {
        const nextIndex = currentIndexRef.current + 1;
        if (nextIndex >= allCardsRef.current.length) {
          onClose();
        } else {
          navigateToContract(nextIndex);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, navigateToContract]);

  // Prevent body scroll when story view is open
  useEffect(() => {
    // Lock both html and body scroll with CSS class
    document.documentElement.classList.add('story-view-open');
    document.body.classList.add('story-view-open');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.documentElement.classList.remove('story-view-open');
      document.body.classList.remove('story-view-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  if (allCards.length === 0) {
    return (
      <div className="card-story-view show">
        <button className="card-close-btn" onClick={onClose}>×</button>
        <div className="mobile-loading-container">
          <div className="mobile-loading-text">No contracts to display</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-story-view show no-scroll">
      {/* Grouped Progress Bar */}
      <StoryProgressBar
        tickerGroups={tickerGroups}
        currentTicker={currentTicker}
        currentIndexInTicker={currentIndexInTicker}
        allTickers={allTickers}
      />

      {/* Close Button */}
      <button className="card-close-btn" onClick={onClose}>×</button>

      {/* Card Carousel */}
      <div className="card-carousel-container">
        <div
          className="card-wrapper"
          style={{
            transform: `translateX(-${currentGlobalIndex * 100}%)`,
            transition: 'transform 0.3s ease'
          }}
        >
          {allCards.map((contract) => (
            <div key={contract.globalIndex} className="story-card-wrapper">
              <ContractCard
                contract={contract}
                expirationSettings={expirationSettings}
                onGainUpdate={onContractGainUpdate}
                onUpdateAll={onUpdateAll}
                originalSettings={originalSettings}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrow Buttons */}
      <div className="story-nav-arrows">
        <button
          className="story-nav-btn story-nav-prev"
          onClick={handlePrevious}
          disabled={currentGlobalIndex === 0}
          aria-label="Previous contract"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          className="story-nav-btn story-nav-next"
          onClick={handleNext}
          aria-label="Next contract"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TickerStoryView;
